/**
 * ============================================================================
 * PERMISSIONS API TYPES — mirrors the GetPermissions Smithy shapes.
 * ============================================================================
 *
 * Source of truth: HILTBusinessReportingServiceModel
 * model/commons/permissionStructures.smithy + model/requests|responses/
 * getPermissions*.smithy (CR-298499539). The caller names the actor to
 * describe; Midway callers are overridden server-side to their authenticated
 * alias, so the client-sent actor is a hint, never trusted.
 */

/** BRASS actor types: a Midway human ("principal") or an AWS account ("aws"). */
export type ActorType = 'principal' | 'aws';

/** Sentinel requiredBindleLockId for an open (no-lock) dashboard/feature. */
export const NO_LOCK_SENTINEL = 'NONE';

/** Wire id of the synthetic cross-cutting ALL grant entry (not a real dashboard/feature). */
export const ALL_GRANT_ID = 'all';

export interface OperationAccess {
  /** Operation category id: "view" | "publish" | "admin". */
  id: string;
  name: string;
  /** Human-readable description (UI tooltips). */
  description: string;
  accessible: boolean;
  /** Bindle Lock resource id granting the category (operations always have one). */
  requiredBindleLockId: string;
}

export interface DashboardAccess {
  /** Dashboard wire id (e.g. "fai", "quality"); the synthetic ALL grant uses "all". */
  dashboardId: string;
  /** Display name for pickers. */
  name: string;
  /** Review types served, display order; empty for the "all" entry. */
  supportedReviewTypes: string[];
  accessible: boolean;
  /** Bindle Lock resource id, or the "NONE" sentinel when the dashboard is open. */
  requiredBindleLockId: string;
}

export interface FeatureAccess {
  /** Feature wire id (e.g. "wbr_callouts"); the synthetic ALL grant uses "all". */
  featureId: string;
  name: string;
  description: string;
  accessible: boolean;
  /** Bindle Lock resource id, or the "NONE" sentinel when the feature is open. */
  requiredBindleLockId: string;
}

export interface GetPermissionsInput {
  /** Amazon alias ("principal") or AWS account id ("aws"). Required, non-blank. */
  actorId: string;
  actorType: ActorType;
}

export interface GetPermissionsOutput {
  /** Echo of the RESOLVED actor (Midway callers: the authenticated alias). */
  actorId: string;
  actorType: ActorType;
  /** Empty for an "aws" actor (resource-policy-governed, not discoverable). */
  accessibleOperations: OperationAccess[];
  dashboards: DashboardAccess[];
  features: FeatureAccess[];
}
